"use client"

import { useTranslations } from "next-intl"
import { startTransition, useState } from "react"
import { useActionState } from "react"

import { updateMerchantProfileAction } from "@/app/[locale]/merchant/actions"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { useToast } from "@/hooks/use-toast"
import type { MerchantProfile } from "@/lib/zenstack/models"
import type { TMerchantProfileCreateData } from "@/schemas/merchant/MerchantProfileSchemas"

import { MerchantProfileForm } from "./MerchantProfileForm"
import { useProgressBar } from "@/hooks/useGlobalStore"

interface MerchantProfileUpdateProps {
  merchantProfile: MerchantProfile
  showUpdatePassword?: boolean
  children?: React.ReactNode
  isOpen?: boolean
  setIsOpen?: (isOpen: boolean) => void
}

export function MerchantProfileUpdate({
  merchantProfile,
  showUpdatePassword,
  children,
  isOpen,
  setIsOpen,
}: MerchantProfileUpdateProps) {
  const { toast } = useToast()
  const { startProgress, completeProgress } = useProgressBar()
  const t = useTranslations("merchant.profile")
  const [loadingProgress] = useState(0)

  const [_, updateMerchantProfile, isUpdatePending] = useActionState(
    async (_: unknown, data: TMerchantProfileCreateData) => {
      const result = await updateMerchantProfileAction({
        ...data,
        id: merchantProfile.id,
      })
      completeProgress()

      if (result && result.success) {
        toast({
          title: t("toast.profile_updated.title"),
          description: t("toast.profile_updated.description"),
        })
      }
      return result
    },
    null
  )

  const handleSubmit = async (data: TMerchantProfileCreateData) => {
    startProgress()
    startTransition(() => {
      updateMerchantProfile(data)
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {!isOpen && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4">
          <DialogTitle>{t("update.title")}</DialogTitle>
          <DialogDescription>{t("update.description")}</DialogDescription>
        </DialogHeader>

        <MerchantProfileForm
          merchantType={merchantProfile.merchantType}
          merchantProfile={merchantProfile as TMerchantProfileCreateData}
          onSubmit={handleSubmit}
          isLoading={isUpdatePending}
          loadingProgress={loadingProgress}
          showUpdatePassword={showUpdatePassword}
        />
      </DialogContent>
    </Dialog>
  )
}
